import { Link } from '@tanstack/react-router';
import { ArrowRightIcon, Gamepad2Icon } from 'lucide-react';
import type { SyntheticEvent } from 'react';

const MASK_STYLE = {
  WebkitMaskImage: '-webkit-radial-gradient(white, black)',
  maskImage: 'radial-gradient(white, black)',
  WebkitMaskComposite: 'source-in',
  maskComposite: 'intersect'
} as const;

interface ScaleCtaCardProps {
  to?: string;
  params?: Record<string, string>;
  imageSrc: string;
  imageAlt: string;
  title: string;
  ctaLabel?: string;
  ctaPosition?: 'corner' | 'center';
  buttonColor?: string;
  defaultImage: string;
  disabled?: boolean;
  statusLabel?: string;
  unavailable?: boolean;
  bottomMessage?: string;
}

const ScaleCtaCard = ({
  to,
  params,
  imageSrc,
  imageAlt,
  title,
  ctaLabel,
  ctaPosition = 'corner',
  buttonColor = 'primary',
  defaultImage,
  disabled = false,
  statusLabel,
  unavailable = false,
  bottomMessage
}: ScaleCtaCardProps) => {
  const handleImageError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = defaultImage;
    event.currentTarget.onerror = null;
  };

  const buttonColorClass = `bg-${buttonColor}`;
  const isInactive = disabled || unavailable;
  const overlayOpacity = isInactive ? 'bg-dark/80' : 'bg-black/35';
  const cardClasses = isInactive
    ? 'overflow-hidden relative h-60 rounded-3xl cursor-not-allowed group text-background'
    : 'relative h-60 rounded-3xl text-background group';
  const showCta = !isInactive && ctaLabel;

  const content = (
    <>
      <img
        src={imageSrc}
        alt={imageAlt}
        className="object-cover absolute w-full h-full"
        onError={handleImageError}
      />

      <div className={`absolute top-0 left-0 w-full h-full ${overlayOpacity}`} />

      {statusLabel && (
        <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-background/90 text-foreground text-sm font-medium z-10">
          {statusLabel}
        </div>
      )}

      {bottomMessage && (
        <div className="absolute bottom-0 left-0 z-20 py-1 w-full text-center transition duration-300 ease-in-out translate-y-full bg-warning group-hover:translate-y-0">
          {bottomMessage}
        </div>
      )}

      <div className="flex overflow-hidden relative justify-center items-center w-full h-full">
        {showCta && ctaPosition === 'corner' && (
          <>
            <div
              className={`absolute bottom-2.5 right-2.5 rounded-full ${buttonColorClass} w-10 aspect-square transition-transform duration-300 ease-in-out group-hover:scale-[15]`}
            />

            <div className="absolute bottom-2.5 right-2.5 flex flex-nowrap">
              <div className="flex opacity-0 items-center pl-2.5 group-hover:opacity-100 transition-all duration-300 ease-in-out">
                {ctaLabel}
              </div>

              <div className="flex justify-center items-center h-10 min-w-10 aspect-square">
                <ArrowRightIcon />
              </div>
            </div>
          </>
        )}

        {showCta && ctaPosition === 'center' && (
          <>
            <div
              className={`absolute left-1/2 w-10 rounded-full transition-transform duration-300 ease-in-out -translate-x-1/2 bottom-[1.5rem] ${buttonColorClass} aspect-square group-hover:scale-[12]`}
            />

            <div className="absolute bottom-[1.5rem] left-1/2 flex flex-nowrap items-center -translate-x-1/2">
              <div className="text-lg flex overflow-hidden items-center max-w-0 opacity-0 group-hover:max-w-[200px] group-hover:opacity-100 transition-all duration-300 ease-in-out whitespace-nowrap">
                {ctaLabel}&nbsp;
              </div>

              <div className="flex justify-center items-center h-10">
                <Gamepad2Icon />
              </div>
            </div>
          </>
        )}

        <h2 className="absolute top-1/2 left-1/2 z-10 px-8 w-full text-2xl md:text-3xl xl:text-4xl font-medium text-center -translate-x-1/2 -translate-y-1/2 [overflow-wrap:anywhere] hyphens-auto">
          {title}
        </h2>
      </div>
    </>
  );

  if (isInactive) {
    return (
      <div className={cardClasses} style={MASK_STYLE}>
        {content}
      </div>
    );
  }

  if (!to) {
    return (
      <div className={cardClasses} style={MASK_STYLE}>
        {content}
      </div>
    );
  }

  return (
    <Link to={to} params={params} className={cardClasses} style={MASK_STYLE}>
      {content}
    </Link>
  );
};

export { ScaleCtaCard };
